import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import type { SchemeMechanic, ExternalVerification, ExtractionClassification } from '@infoscend-claims/shared';
import {
  ConfidenceBadge,
  MadLibsEditor,
  TaxBlock,
  FundAllocationBlock,
  ExternalVerificationPanel,
  SchemeIcon,
} from '../../components/scheme/SchemeEditor.js';
import { ARCHETYPE_DEFINITIONS } from '../../lib/schemeArchetypes.js';
import { ExtractionClassificationCard } from '../../components/admin/ExtractionClassificationCard.js';

// ── Types matching API ExtractedSchemeProposal ────────────────────────────────

interface ExtractedMechanicProposal {
  baselineUnit: { value: string; confidence: number };
  measurementWindow: {
    type: 'FIXED_PERIOD' | 'ROLLING' | 'POINT_IN_TIME';
    startDate: string | null;
    endDate: string | null;
    frequency: 'MONTHLY' | 'QUARTERLY' | 'ONE_TIME' | null;
    confidence: number;
  };
  eligibility: { value: { logicalOperator: 'AND' | 'OR'; conditions: unknown[] }; confidence: number };
  payout: { value: unknown; confidence: number; fieldConfidences: Record<string, number> };
  constraints: { value: unknown; confidence: number };
  fundAllocation: { glAccountCode: string | null; budgetCapAmount: number | null; budgetCurrency: string | null; confidence: number };
  suggestedArchetype: string | null;
}

interface ExtractedProposal {
  schemeCode: { value: string; confidence: number; suggestion?: boolean };
  schemeType: { value: string; confidence: number };
  name: { value: string; confidence: number };
  description: { value: string; confidence: number };
  mechanic: ExtractedMechanicProposal;
  taxTreatment: {
    value: 'GROSS_UP' | 'NET_DEDUCT' | 'EXEMPT';
    pphCategory: 'PPH_23_SERVICE' | 'PPH_4_2_FINAL' | 'NONE';
    taxWithholdingPercent: number;
    confidence: number;
  };
  eligibility: {
    scope: 'PLATFORM_WIDE' | 'SPECIFIC_DISTRIBUTORS' | 'SPECIFIC_GROUP' | 'UNCLEAR';
    distributorOrgIds: string[];
    distributorGroupId: string | null;
    confidence: number;
    rawAINames: string[];
    unresolvedNames: string[];
  };
  validFrom: { value: string | null; confidence: number };
  validTo: { value: string | null; confidence: number };
  budgetCap: { value: number | null; confidence: number };
  sourceS3Key: string | null;
  rawExtractionLogId: string;
  classification: ExtractionClassification | null;
}

const SCHEME_TYPES = [
  'VISIBILITY', 'VOLUME_REBATE', 'SAMPLING_ACTIVITY', 'NPD_LISTING',
  'COOP_MARKETING', 'PROMO_DISCOUNT_SUBSIDY', 'TRADE_SPEND_POOL',
  'MEMO_REDEMPTION', 'OTHER',
];

// ── Field wrapper ─────────────────────────────────────────────────────────────

function FieldRow({ label, confidence, children }: { label: string; confidence?: number; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-500">{label}</label>
        {confidence !== undefined && <ConfidenceBadge confidence={confidence} label={`${Math.round(confidence * 100)}%`} />}
      </div>
      {children}
    </div>
  );
}

// ── Build SchemeMechanic from proposal ────────────────────────────────────────

function buildMechanicFromProposal(m: ExtractedMechanicProposal): SchemeMechanic {
  return {
    baselineUnit: (m.baselineUnit?.value as SchemeMechanic['baselineUnit']) ?? 'OUTLET',
    measurementWindow: {
      type: m.measurementWindow?.type ?? 'FIXED_PERIOD',
      startDate: m.measurementWindow?.startDate ?? null,
      endDate: m.measurementWindow?.endDate ?? null,
      frequency: m.measurementWindow?.frequency ?? 'MONTHLY',
    },
    eligibility: (m.eligibility?.value as SchemeMechanic['eligibility']) ?? { logicalOperator: 'AND' as const, conditions: [] },
    payout: (m.payout?.value as SchemeMechanic['payout']) ?? {
      type: 'FIXED', currency: 'IDR', taxTreatment: 'NET_DEDUCT',
      taxWithholdingPercent: 0.02, pphCategory: 'PPH_23_SERVICE', amount: 0,
    },
    constraints: (m.constraints?.value as SchemeMechanic['constraints']) ?? {
      stackability: { rule: 'FULLY_STACKABLE' },
      externalVerifications: [],
      targetEntities: {},
    },
    fundAllocation: {
      glAccountCode: m.fundAllocation?.glAccountCode ?? null,
      budgetCap: (m.fundAllocation?.budgetCapAmount ?? null) != null
        ? { amount: m.fundAllocation!.budgetCapAmount!, currency: 'IDR' as const }
        : null,
    },
  };
}

// ── Main component ────────────────────────────────────────────────────────────

export function SchemeUploadPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [inputMode, setInputMode] = useState<'file' | 'text'>('file');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pastedText, setPastedText] = useState('');
  const [dragging, setDragging] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<ExtractedProposal | null>(null);

  // Editable form state (populated from proposal)
  const [formCode, setFormCode] = useState('');
  const [formType, setFormType] = useState('');
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formValidFrom, setFormValidFrom] = useState('');
  const [formValidTo, setFormValidTo] = useState('');
  const [formStatus, setFormStatus] = useState('ACTIVE');
  const [formEligibilityScope, setFormEligibilityScope] = useState<string>('PLATFORM_WIDE');

  // Mechanic (structured — not JSON string)
  const [mechanic, setMechanic] = useState<SchemeMechanic | null>(null);

  // Tax
  const [taxTreatment, setTaxTreatment] = useState('NET_DEDUCT');
  const [pphCategory, setPphCategory] = useState('PPH_23_SERVICE');
  const [withholdingPercent, setWithholdingPercent] = useState('2.00');

  // Fund
  const [glAccountCode, setGlAccountCode] = useState('');
  const [sapWbsElement, setSapWbsElement] = useState('');
  const [budgetCapAmount, setBudgetCapAmount] = useState('');

  // External verifications
  const [externalVerifications, setExternalVerifications] = useState<ExternalVerification[]>([]);

  // AI classification metadata (confidence, signals, alternates)
  const [classification, setClassification] = useState<ExtractionClassification | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Drag-and-drop ─────────────────────────────────────────────────────────

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) setSelectedFile(file);
  }, []);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
  };

  // ── Extract ───────────────────────────────────────────────────────────────

  const handleExtract = async () => {
    setExtractError(null);
    setExtracting(true);
    setProposal(null);
    setMechanic(null);
    setClassification(null);

    try {
      let body: Record<string, unknown>;

      if (inputMode === 'text') {
        body = { inputType: 'text', text: pastedText };
      } else {
        if (!selectedFile) { setExtractError('No file selected'); setExtracting(false); return; }
        const arrayBuffer = await selectedFile.arrayBuffer();
        const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
        body = {
          inputType: 'file',
          mimeType: selectedFile.type || 'application/octet-stream',
          fileData: base64,
        };
      }

      const result = await api.post<ExtractedProposal>('/admin/schemes/upload', body);
      setProposal(result);
      setClassification(result.classification ?? null);

      // Prefill metadata
      setFormCode(result.schemeCode.value);
      setFormType(result.schemeType.value);
      setFormName(result.name.value);
      setFormDesc(result.description.value);
      setFormValidFrom(result.validFrom.value ?? '');
      setFormValidTo(result.validTo.value ?? '');
      setFormEligibilityScope(result.eligibility.scope);

      // Prefill mechanic (structured)
      setMechanic(buildMechanicFromProposal(result.mechanic));

      // Prefill tax
      setTaxTreatment(result.taxTreatment.value);
      setPphCategory(result.taxTreatment.pphCategory);
      setWithholdingPercent(String(result.taxTreatment.taxWithholdingPercent * 100));

      // Prefill fund allocation
      setGlAccountCode(result.mechanic.fundAllocation.glAccountCode ?? '');
      setBudgetCapAmount(result.mechanic.fundAllocation.budgetCapAmount != null ? String(result.mechanic.fundAllocation.budgetCapAmount) : '');

    } catch (err) {
      setExtractError(err instanceof Error ? err.message : 'Extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!proposal || !mechanic) return;
    setSaveError(null);
    setSaving(true);

    try {
      const mechanicWithFund: SchemeMechanic = {
        ...mechanic,
        constraints: {
          ...(mechanic.constraints ?? { stackability: { rule: 'FULLY_STACKABLE' }, targetEntities: {} }),
          externalVerifications,
        },
        fundAllocation: {
          glAccountCode: glAccountCode || null,
          budgetCap: budgetCapAmount ? { amount: Number(budgetCapAmount), currency: 'IDR' as const } : null,
          ...(sapWbsElement ? { sapWbsElement } : {}),
        },
      };

      const eligibilityFilter: Record<string, unknown> = {};
      if (formEligibilityScope === 'SPECIFIC_DISTRIBUTORS' && proposal.eligibility.distributorOrgIds.length > 0) {
        eligibilityFilter['distributorOrgIds'] = proposal.eligibility.distributorOrgIds;
      } else if (formEligibilityScope === 'SPECIFIC_GROUP' && proposal.eligibility.distributorGroupId) {
        eligibilityFilter['distributorGroupId'] = proposal.eligibility.distributorGroupId;
      }

      await api.post('/admin/schemes', {
        schemeCode: formCode,
        schemeType: formType,
        name: formName,
        description: formDesc,
        mechanic: mechanicWithFund,
        isoCurrencyCode: 'IDR',
        validFrom: formValidFrom,
        validTo: formValidTo,
        budgetCap: budgetCapAmount ? Number(budgetCapAmount) : null,
        budgetCapAmount: budgetCapAmount ? Number(budgetCapAmount) : null,
        glAccountCode: glAccountCode || null,
        sapWbsElement: sapWbsElement || null,
        taxTreatment,
        pphCategory,
        taxWithholdingPercent: Number(withholdingPercent) / 100,
        externalVerifications: externalVerifications.length > 0 ? externalVerifications : null,
        status: formStatus,
        eligibilityFilter,
        extractedFromS3Key: proposal.sourceS3Key,
        rawExtractionLogId: proposal.rawExtractionLogId,
        ...(classification ? { extractionMetadata: { classification } } : {}),
      });

      setTimeout(() => navigate('/admin/schemes'), 800);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Confidence check ──────────────────────────────────────────────────────

  const lowPayoutConfidence = proposal
    ? (proposal.mechanic.payout.confidence < 0.5 ||
       Object.values(proposal.mechanic.payout.fieldConfidences).some((c) => c < 0.5))
    : false;

  const canExtract = inputMode === 'text' ? pastedText.length >= 10 : !!selectedFile;
  const canSave = !saving && !!formCode && !!formType && !!formName && !!formValidFrom && !!formValidTo && !lowPayoutConfidence;

  function handleTaxField(field: string, val: string) {
    if (field === 'taxTreatment') setTaxTreatment(val);
    else if (field === 'pphCategory') setPphCategory(val);
    else if (field === 'withholdingPercent') setWithholdingPercent(val);
  }

  function handleFundField(field: string, val: string) {
    if (field === 'glAccountCode') setGlAccountCode(val);
    else if (field === 'sapWbsElement') setSapWbsElement(val);
    else if (field === 'budgetCapAmount') setBudgetCapAmount(val);
  }

  // Suggested archetype details
  const suggestedArch = proposal?.mechanic.suggestedArchetype
    ? ARCHETYPE_DEFINITIONS.find((a) => a.key === proposal.mechanic.suggestedArchetype)
    : null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-8">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/admin/schemes')}
          className="text-xs text-slate-400 hover:text-copper transition-colors mb-4 block"
        >
          ← {t('admin.schemes.title')}
        </button>
        <span className="inline-block text-[11px] font-semibold tracking-[0.22em] uppercase text-copper mb-2">
          {t('admin.title')}
        </span>
        <h1 className="font-serif italic text-3xl text-ink">{t('admin.schemes.uploadMemo')}</h1>
        <p className="mt-1 text-slate-500 text-sm">{t('admin.schemes.uploadIntro')}</p>
      </div>

      {/* Step 1 — Upload */}
      <div className="bg-white border border-hairline rounded p-6 space-y-4">
        <h2 className="text-sm font-semibold text-ink">1. {t('admin.schemes.uploadMemo')}</h2>

        {/* Toggle */}
        <div className="flex gap-1 bg-bone rounded p-0.5 w-fit">
          {(['file', 'text'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setInputMode(mode)}
              className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${inputMode === mode ? 'bg-white text-ink shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              {mode === 'file' ? t('admin.schemes.uploadFile') : t('admin.schemes.pasteText')}
            </button>
          ))}
        </div>

        {inputMode === 'file' ? (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${dragging ? 'border-copper bg-copper/5' : 'border-hairline hover:border-copper/50'}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={onFileChange}
            />
            <svg className="mx-auto mb-2 text-slate-400" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            {selectedFile ? (
              <p className="text-sm font-medium text-ink">{selectedFile.name}</p>
            ) : (
              <>
                <p className="text-sm text-slate-500">{t('admin.schemes.dropFile')}</p>
                <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG — max 25 MB</p>
              </>
            )}
          </div>
        ) : (
          <textarea
            className="w-full h-40 border border-hairline rounded p-3 text-sm font-mono resize-y focus:outline-none focus:ring-1 focus:ring-copper"
            placeholder={t('admin.schemes.pasteTextPlaceholder')}
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
          />
        )}

        {extractError && <p className="text-danger text-sm">{extractError}</p>}

        <div className="flex gap-3">
          <button
            onClick={handleExtract}
            disabled={!canExtract || extracting}
            className="px-4 py-2 bg-copper text-white text-sm font-semibold rounded hover:bg-copper-deep transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {extracting ? t('common.processing') : t('admin.schemes.extract')}
          </button>
          {proposal && (
            <button
              onClick={handleExtract}
              disabled={!canExtract || extracting}
              className="text-sm text-copper hover:underline"
            >
              {t('admin.schemes.reExtract')}
            </button>
          )}
        </div>
      </div>

      {/* Step 2 — AI Proposed Form */}
      {proposal && mechanic && (
        <>
          {/* Suggested archetype banner */}
          {suggestedArch && (
            <div className="flex items-center gap-3 bg-copper/5 border border-copper/20 rounded px-4 py-3">
              <div className="text-copper">
                <SchemeIcon name={suggestedArch.lucideIcon} size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-copper">
                  {t('admin.schemes.aiSuggestsArchetype', { defaultValue: 'AI suggests this matches:' })}
                  {' '}
                  <span className="font-serif italic">
                    {t(`admin.schemes.archetypes.${suggestedArch.key}.name`, { defaultValue: suggestedArch.key.replace(/_/g, ' ') })}
                  </span>
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {t(`admin.schemes.archetypes.${suggestedArch.key}.description`, { defaultValue: '' })}
                </p>
              </div>
              <ConfidenceBadge confidence={proposal.schemeType.confidence} label={`${Math.round(proposal.schemeType.confidence * 100)}% confident`} />
            </div>
          )}

          {/* Low payout confidence warning */}
          {lowPayoutConfidence && (
            <div className="flex items-start gap-3 bg-danger/5 border border-danger/30 rounded px-4 py-3">
              <span className="text-danger text-sm mt-0.5">⚠</span>
              <div>
                <p className="text-sm font-semibold text-danger">
                  {t('admin.schemes.lowPayoutConfidence', { defaultValue: 'AI could not confidently extract payout details' })}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {t('admin.schemes.lowPayoutConfidenceHelp', { defaultValue: 'Please review and correct the payout rule below before saving.' })}
                </p>
              </div>
            </div>
          )}

          {/* Unresolved distributors */}
          {proposal.eligibility.unresolvedNames.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
              {t('admin.schemes.unresolvedDistributors', { names: proposal.eligibility.unresolvedNames.join(', ') })}
            </div>
          )}

          {/* Metadata fields */}
          <div className="bg-white border border-hairline rounded p-6 space-y-6">
            <div className="flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600">
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
              </svg>
              <h2 className="text-sm font-semibold text-ink">{t('admin.schemes.aiProposed')}</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Column 1 — Metadata */}
              <div className="space-y-4">
                <FieldRow label={t('admin.schemes.schemeCode')} confidence={proposal.schemeCode.confidence}>
                  <input
                    className="w-full border border-hairline rounded px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-copper"
                    value={formCode}
                    onChange={(e) => setFormCode(e.target.value)}
                  />
                </FieldRow>

                <FieldRow label={t('admin.schemes.type')} confidence={proposal.schemeType.confidence}>
                  <select
                    className="w-full border border-hairline rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-copper"
                    value={formType}
                    onChange={(e) => setFormType(e.target.value)}
                  >
                    {SCHEME_TYPES.map((st) => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                </FieldRow>

                <FieldRow label={t('admin.schemes.name')} confidence={proposal.name.confidence}>
                  <input
                    className="w-full border border-hairline rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-copper"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                  />
                </FieldRow>

                <FieldRow label={t('admin.schemes.description')} confidence={proposal.description.confidence}>
                  <textarea
                    className="w-full border border-hairline rounded px-3 py-1.5 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-copper"
                    rows={3}
                    value={formDesc}
                    onChange={(e) => setFormDesc(e.target.value)}
                  />
                </FieldRow>

                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label={t('admin.schemes.validFrom')} confidence={proposal.validFrom.confidence}>
                    <input
                      type="date"
                      className="w-full border border-hairline rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-copper"
                      value={formValidFrom}
                      onChange={(e) => setFormValidFrom(e.target.value)}
                    />
                  </FieldRow>
                  <FieldRow label={t('admin.schemes.validTo')} confidence={proposal.validTo.confidence}>
                    <input
                      type="date"
                      className="w-full border border-hairline rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-copper"
                      value={formValidTo}
                      onChange={(e) => setFormValidTo(e.target.value)}
                    />
                  </FieldRow>
                </div>

                <FieldRow label={t('admin.schemes.status')}>
                  <select
                    className="w-full border border-hairline rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-copper"
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value)}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </FieldRow>
              </div>

              {/* Column 2 — Eligibility scope */}
              <div className="space-y-4">
                <FieldRow label={t('admin.schemes.eligibility')} confidence={proposal.eligibility.confidence}>
                  <div className="space-y-1.5">
                    {(['PLATFORM_WIDE', 'SPECIFIC_DISTRIBUTORS', 'SPECIFIC_GROUP', 'UNCLEAR'] as const).map((scope) => (
                      <label key={scope} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="eligibilityScope"
                          value={scope}
                          checked={formEligibilityScope === scope}
                          onChange={(e) => setFormEligibilityScope(e.target.value)}
                          className="accent-copper"
                        />
                        <span className="text-sm text-slate-700">{t(`admin.schemes.eligibilityScope.${scope.toLowerCase().replace(/_/g, '')}`, { defaultValue: scope })}</span>
                      </label>
                    ))}
                  </div>

                  {formEligibilityScope === 'SPECIFIC_DISTRIBUTORS' && proposal.eligibility.rawAINames.length > 0 && (
                    <div className="mt-2 bg-bone border border-hairline rounded p-2 text-xs text-slate-600">
                      <span className="font-semibold">AI found: </span>
                      {proposal.eligibility.rawAINames.join(', ')}
                      {proposal.eligibility.unresolvedNames.length > 0 && (
                        <span className="text-amber-600"> ({proposal.eligibility.unresolvedNames.length} unresolved)</span>
                      )}
                    </div>
                  )}
                </FieldRow>

                {/* Payout confidence breakdown */}
                {Object.keys(proposal.mechanic.payout.fieldConfidences).length > 0 && (
                  <div className="bg-bone border border-hairline rounded p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                      {t('admin.schemes.payoutFieldConfidences', { defaultValue: 'Payout Field Confidence' })}
                    </p>
                    <div className="space-y-0.5">
                      {Object.entries(proposal.mechanic.payout.fieldConfidences).map(([field, conf]) => (
                        <div key={field} className="flex justify-between text-[10px]">
                          <span className="text-slate-500 font-mono">{field}</span>
                          <ConfidenceBadge confidence={conf} label={`${Math.round(conf * 100)}%`} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* AI classification card — confidence, signals, alternates */}
          <ExtractionClassificationCard classification={classification} />

          {/* Mad-Libs payout editor */}
          <div className="bg-white border border-hairline rounded p-6">
            <MadLibsEditor mechanic={mechanic} onChange={setMechanic} readOnly={false} />
          </div>

          {/* External verifications */}
          {(externalVerifications.length > 0) && (
            <div className="bg-white border border-hairline rounded p-6">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">
                {t('admin.schemes.externalVerifications.title', { defaultValue: 'External Verifications' })}
              </h3>
              <ExternalVerificationPanel
                verifications={externalVerifications}
                onChange={setExternalVerifications}
                readOnly={false}
              />
            </div>
          )}

          {/* Tax block */}
          <div className="bg-white border border-hairline rounded p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                {t('admin.schemes.tax.title', { defaultValue: 'Tax Treatment' })}
              </span>
              <ConfidenceBadge confidence={proposal.taxTreatment.confidence} label={`${Math.round(proposal.taxTreatment.confidence * 100)}% confident`} />
            </div>
            <TaxBlock
              taxTreatment={taxTreatment}
              pphCategory={pphCategory}
              withholdingPercent={withholdingPercent}
              onChange={handleTaxField}
              readOnly={false}
            />
          </div>

          {/* Fund allocation block */}
          <div className="bg-white border border-hairline rounded p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                {t('admin.schemes.fund.title', { defaultValue: 'Fund Allocation' })}
              </span>
              <ConfidenceBadge confidence={proposal.mechanic.fundAllocation.confidence} label={`${Math.round(proposal.mechanic.fundAllocation.confidence * 100)}% confident`} />
            </div>
            <FundAllocationBlock
              glAccountCode={glAccountCode}
              sapWbsElement={sapWbsElement}
              budgetCapAmount={budgetCapAmount}
              onChange={handleFundField}
              readOnly={false}
            />
          </div>

          {/* Action bar */}
          {saveError && <p className="text-danger text-sm">{saveError}</p>}

          <div className="flex items-center gap-4 pt-2 border-t border-hairline">
            <button
              onClick={handleSave}
              disabled={!canSave}
              className="px-5 py-2 bg-copper text-white text-sm font-semibold rounded hover:bg-copper-deep transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? t('admin.schemes.saving') : t('admin.schemes.save')}
            </button>
            <button
              onClick={() => navigate('/admin/schemes')}
              className="text-sm text-slate-400 hover:text-slate-600"
            >
              {t('admin.schemes.cancel')}
            </button>
            {lowPayoutConfidence && (
              <span className="text-xs text-danger">
                ⚠ {t('admin.schemes.lowPayoutConfidence', { defaultValue: 'Fix payout fields before saving' })}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
